import { useEffect } from 'react';

/**
 * Hook to track iOS keyboard using Visual Viewport API
 * Applies transform directly to the provided element to move with keyboard
 */
export function useVisualViewport(ref?: HTMLElement | null, options: { enabled?: boolean } = {}) {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!ref || !viewport || options.enabled === false) return;

    let rafId = 0;

    const update = () => {
      const vp = window.visualViewport!;
      // Calculate how much the visual viewport shrinks vs layout viewport
      const raw = window.innerHeight - vp.height - (vp.offsetTop || 0);
      const offset = Math.max(0, raw);
      // GPU-accelerated transform to avoid layout thrashing
      ref.style.transform = offset > 0 ? `translate3d(0, -${offset}px, 0)` : '';
      ref.style.willChange = 'transform';
    };

    const onChange = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    viewport.addEventListener('resize', onChange);
    viewport.addEventListener('scroll', onChange);

    // Initial call
    onChange();

    return () => {
      viewport.removeEventListener('resize', onChange);
      viewport.removeEventListener('scroll', onChange);
      cancelAnimationFrame(rafId);
      ref.style.transform = '';
      ref.style.willChange = '';
    };
  }, [ref, options.enabled]);
}
